import { useId, type ReactNode } from 'react'
import { Check } from 'lucide-react'

import { parsePKR, formatPKR } from '@cattle/money'

import { cn } from '../lib/cn'
import { t } from '../lib/i18n'

/* ── Field — label, hint, error, and the control it wraps ─────────────────
   Every field states what it changes downstream. That is the design's rule for
   these consoles, so the hint is part of the primitive, not an afterthought.
   ────────────────────────────────────────────────────────────────────────── */

export interface FieldProps {
  label: ReactNode
  hint?: ReactNode
  error?: string
  required?: boolean
  htmlFor?: string
  children: ReactNode
  /** Two-column row (label beside control) — the deck's `.frow`. */
  layout?: 'stacked' | 'row'
  className?: string
}

export function Field({
  label,
  hint,
  error,
  required,
  htmlFor,
  children,
  layout = 'stacked',
  className,
}: FieldProps) {
  if (layout === 'row') {
    return (
      <div
        className={cn(
          'grid items-center gap-[18px] border-b border-muted/15 py-[15px] last:border-b-0',
          'md:grid-cols-[230px_1fr]',
          className,
        )}
      >
        <div>
          <label htmlFor={htmlFor} className="text-[13.5px] font-semibold text-dark">
            {label}
            {required ? <span className="text-danger"> *</span> : null}
          </label>
          {hint ? (
            <small className="mt-0.5 block text-[11.5px] leading-snug text-muted">{hint}</small>
          ) : null}
        </div>
        <div>
          {children}
          {error ? <p className="mt-1.5 text-xs text-danger">{error}</p> : null}
        </div>
      </div>
    )
  }

  return (
    <div className={cn('mb-3.5', className)}>
      <label htmlFor={htmlFor} className="mb-1.5 block text-[13px] font-semibold text-dark">
        {label}
        {required ? <span className="text-danger"> *</span> : null}
      </label>
      {children}
      {hint ? <p className="mt-1.5 text-xs leading-snug text-muted">{hint}</p> : null}
      {error ? <p className="mt-1.5 text-xs text-danger">{error}</p> : null}
    </div>
  )
}

const CONTROL =
  'flex h-10 w-full max-w-[340px] items-center gap-2 rounded-[10px] border border-glass-border bg-glass px-3.5 text-[13.5px] font-semibold text-dark outline-none placeholder:font-medium placeholder:text-light'

/* ── TextInput ────────────────────────────────────────────────────────────── */

export function TextInput({
  id,
  value,
  onChange,
  placeholder,
  ariaLabel,
  className,
  autoComplete = 'off',
  type = 'text',
}: {
  id?: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  ariaLabel?: string
  className?: string
  autoComplete?: string
  /** `password` masks the field. Nothing else here needs a different input. */
  type?: 'text' | 'password'
}) {
  return (
    <input
      id={id}
      type={type}
      value={value}
      autoComplete={autoComplete}
      aria-label={ariaLabel}
      placeholder={placeholder}
      onChange={(event) => onChange(event.target.value)}
      className={cn(CONTROL, className)}
    />
  )
}

/* ── MoneyInput ───────────────────────────────────────────────────────────
   Holds integer paisa, shows rupees. Parsing goes through `parsePKR` so "3,500"
   and "3500" and "Rs 3,500" all mean the same thing, exactly as they do on the
   phone. Never a float, and never a `<input type="number">` — a spinner on a
   rupee figure invites a mis-click worth thousands.
   ────────────────────────────────────────────────────────────────────────── */

export function MoneyInput({
  id,
  paisa,
  onChange,
  ariaLabel,
  className,
}: {
  id?: string
  paisa: number | undefined
  onChange: (paisa: number | undefined) => void
  ariaLabel?: string
  className?: string
}) {
  const display = paisa === undefined ? '' : formatPKR(paisa, { symbol: false })

  return (
    <div className={cn(CONTROL, 'max-w-[220px]', className)}>
      <span className="font-display text-xs font-bold text-muted">Rs</span>
      <input
        id={id}
        inputMode="numeric"
        value={display}
        aria-label={ariaLabel}
        onChange={(event) => {
          const parsed = parsePKR(event.target.value)
          onChange(parsed === null ? undefined : parsed)
        }}
        className="w-full bg-transparent font-display font-bold tnum outline-none"
      />
    </div>
  )
}

/* ── DateInput ────────────────────────────────────────────────────────────
   `YYYY-MM-DD` text in, `YYYY-MM-DD` text out. It never becomes a `Date`, which
   is what keeps a purchase recorded at 2am on the day it was recorded.
   ────────────────────────────────────────────────────────────────────────── */

export function DateInput({
  id,
  value,
  onChange,
  ariaLabel,
  className,
}: {
  id?: string
  value: string
  onChange: (iso: string) => void
  ariaLabel?: string
  className?: string
}) {
  return (
    <input
      id={id}
      type="date"
      value={value}
      aria-label={ariaLabel}
      onChange={(event) => onChange(event.target.value)}
      className={cn(CONTROL, 'max-w-[200px] tnum', className)}
    />
  )
}

/* ── Select ───────────────────────────────────────────────────────────────
   Options carry codes; labels come from `t()`. A label is never stored.
   ────────────────────────────────────────────────────────────────────────── */

export function Select<Value extends string>({
  id,
  value,
  onChange,
  options,
  ariaLabel,
  className,
}: {
  id?: string
  value: Value
  onChange: (value: Value) => void
  options: { value: Value; label: string }[]
  ariaLabel?: string
  className?: string
}) {
  return (
    <select
      id={id}
      value={value}
      aria-label={ariaLabel}
      onChange={(event) => onChange(event.target.value as Value)}
      className={cn(CONTROL, 'max-w-[260px] appearance-none pe-8', className)}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  )
}

/* ── Toggle ───────────────────────────────────────────────────────────────── */

export function Toggle({
  checked,
  onChange,
  label,
  hint,
  disabled = false,
  className,
}: {
  checked: boolean
  onChange: (checked: boolean) => void
  label: ReactNode
  hint?: ReactNode
  disabled?: boolean
  className?: string
}) {
  const id = useId()
  return (
    <div className={cn('flex items-center gap-3', className)}>
      <div className="min-w-0 flex-1">
        <label htmlFor={id} className="text-[13.5px] font-semibold text-dark">
          {label}
        </label>
        {hint ? <div className="mt-0.5 text-[11.5px] leading-snug text-muted">{hint}</div> : null}
      </div>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cn(
          'relative h-7 w-12 shrink-0 rounded-full transition-colors disabled:opacity-45',
          checked ? 'bg-accent' : 'bg-muted/35',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 size-6 rounded-full bg-white shadow-sm transition-transform',
            'start-0.5',
            checked && 'translate-x-5 rtl:-translate-x-5',
          )}
        />
      </button>
    </div>
  )
}

/* ── RadioCards ───────────────────────────────────────────────────────────
   Side by side, so the two options are COMPARED rather than remembered. The
   phone could not fit this; the desk can, and the choice is consequential.
   ────────────────────────────────────────────────────────────────────────── */

export interface RadioCardOption<Value extends string> {
  value: Value
  title: string
  tag?: string
  points: { text: string; allowed: boolean }[]
}

export function RadioCards<Value extends string>({
  options,
  value,
  onChange,
  name,
  className,
}: {
  options: RadioCardOption<Value>[]
  value: Value | null
  onChange: (value: Value) => void
  name: string
  className?: string
}) {
  return (
    <div className={cn('grid gap-3 md:grid-cols-2', className)} role="radiogroup">
      {options.map((option) => {
        const selected = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            name={name}
            aria-checked={selected}
            onClick={() => onChange(option.value)}
            className={cn(
              'rounded-r2 border p-3.5 text-start transition-colors',
              selected
                ? 'border-accent-glass-border bg-accent-glass'
                : 'border-glass-border bg-glass',
            )}
          >
            <div className="flex items-center gap-2.5">
              <span
                className={cn(
                  'font-display text-base font-bold',
                  selected ? 'text-accent-dark' : 'text-dark',
                )}
              >
                {option.title}
              </span>
              {option.tag ? (
                <span className="rounded-full bg-muted/15 px-2 py-0.5 font-display text-[10.5px] font-bold text-sub">
                  {option.tag}
                </span>
              ) : null}
              {selected ? <Check className="ms-auto size-4 text-accent-dark" /> : null}
            </div>
            <ul className="mt-2.5 flex flex-col gap-1.5">
              {option.points.map((point) => (
                <li
                  key={point.text}
                  className="flex items-start gap-2 text-[12.5px] leading-snug text-sub"
                >
                  <span
                    className={cn(
                      'mt-px flex size-3.5 shrink-0 items-center justify-center',
                      point.allowed ? 'text-success' : 'text-danger',
                    )}
                    aria-hidden="true"
                  >
                    {point.allowed ? '✓' : '✕'}
                  </span>
                  <span>
                    <span className="sr-only">
                      {point.allowed ? t('label.yes') : t('label.no')}:{' '}
                    </span>
                    {point.text}
                  </span>
                </li>
              ))}
            </ul>
          </button>
        )
      })}
    </div>
  )
}
